import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  TableSortLabel,
  Box,
  Grid,
  Tooltip,
} from '@mui/material';
import { useState, useEffect } from 'react';
import UseDurationChips from 'components/styleguide/UseDurationChips';
import { useSelector, useDispatch } from 'react-redux';
import { getAdvancedMfn } from 'redux-core/productions/selectors';
import { useProductionCurrency } from 'utils/hooks';
import { setAdvancedMfn } from 'redux-core/productions/actions';
import { useGetAllTracksQuery } from '../../../../../services/clear/tracks';

const headCells = [
  { id: 'trackTitle', label: 'Track Title', numeric: false, align: 'left' },
  { id: 'trackArtists', label: 'Performer/Library', numeric: false, align: 'left' },
  { id: 'trackUsageCount', label: 'Usage', numeric: true, align: 'left', sortable: false },
  { id: 'trackApprovedFees', label: 'Approved Fee', numeric: true, align: 'left' },
  { id: 'trackSceneAsset', label: 'Scene/Asset', numeric: false, align: 'left' },
];

const rootT = 'drawers.productionSetup.bulkRightsOptions';

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  return [...array].sort(comparator);
}

const TrackList = ({ subprojectId, translate, setHasUnsavedChanges }) => {
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('trackTitle');
  const [selected, setSelected] = useState([]);
  const [allSelected, setAllSelected] = useState(false);

  const advancedMfn = useSelector(getAdvancedMfn);

  const convert = useProductionCurrency();

  const dispatch = useDispatch();

  const { data = [] } = useGetAllTracksQuery(
    { subprojectId },
    {
      refetchOnMountOrArgChange: true,
    },
  );

  useEffect(() => {
    if (data && data.length > 0) {
      const tracks = data.filter((track) => !track.trackUseCustomClearances).map((track) => ({
        trackId: track.trackId,
        linked: track.linked,
        trackUseCustom: track.trackUseCustomClearances,
      }));

      const linkedTracks = tracks.filter(
        (track) => track.linked && !track.trackUseCustom,
      );

      const selectableTracksCount = data.filter(
        (t) => !t.trackUseCustomClearances,
      ).length;

      setAllSelected(linkedTracks.length === selectableTracksCount);
      setSelected(linkedTracks.map((track) => track.trackId));

      dispatch(setAdvancedMfn({ tracks }));
    }
  }, [data, dispatch]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  const handleSelectAllClick = (event) => {
    let filteredTracks = [];
    setHasUnsavedChanges(true);
    if (event.target.checked) {
      const selectableRows = data.filter((track) => !track.trackUseCustomClearances);
      const newSelecteds = selectableRows.map((track) => track.trackId);

      filteredTracks = data
        .filter((track) => !track.trackUseCustomClearances)
        .map((track) => ({ trackId: track.trackId, linked: true }));

      setSelected(newSelecteds);
      setAllSelected(true);
      dispatch(setAdvancedMfn({ tracks: filteredTracks }));
    } else {
      setSelected([]);
      setAllSelected(false);

      filteredTracks = data
        .filter((track) => !track.trackUseCustomClearances)
        .map((track) => ({ trackId: track.trackId, linked: false }));

      dispatch(setAdvancedMfn({ tracks: filteredTracks }));
    }
    event.stopPropagation();
  };

  const handleClick = (checked, id) => {
    setHasUnsavedChanges(true);
    if (checked) {
      setSelected((prevSelected) => [...new Set([...prevSelected, id])]);
      dispatch(
        setAdvancedMfn({
          tracks: advancedMfn.tracks.map((track) => (track.trackId === id ? { ...track, linked: true } : track)),
        }),
      );
    } else {
      setSelected((prevSelected) => prevSelected.filter((trackId) => trackId !== id));
      dispatch(
        setAdvancedMfn({
          tracks: advancedMfn.tracks.map((track) => (track.trackId === id ? { ...track, linked: false } : track)),
        }),
      );
    }
    setAllSelected(selected.length + (checked ? 1 : -1) === data.length);
  };

  const rows = data.map((track) => ({
    id: track.trackId,
    trackTitle: track.trackTitle,
    trackArtists: track.trackArtists,
    trackApprovedFees: track.trackApprovedFees,
    trackSceneAsset: track.trackSceneAsset,
    trackUsage: track.trackUsage,
    linked: track.linked,
    trackUseCustom: track.trackUseCustomClearances,
  }));

  const getTrackUsage = (trackUsage) => {
    if (!trackUsage) return [];

    return trackUsage
      .filter((usage) => usage.requestedType && (usage.requestedDuration || usage.requestedDurationFull === true))
      .map((usage) => ({
        use: usage.requestedType,
        duration: usage.requestedDurationFull === true ? 'FULL' : usage.requestedDuration,
        durationFull: usage.requestedDurationFull,
      }));
  };

  return (
    <TableContainer component={Paper} sx={{ boxShadow: 'none', border: 'none', height: 600 }}>
      <Table sx={{ minWidth: 600 }} aria-label="track list table">
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox">
              <Checkbox
                color="primary"
                checked={allSelected}
                onChange={handleSelectAllClick}
                inputProps={{ 'aria-label': 'select all tracks' }}
              />
            </TableCell>
            {headCells.map((headCell) => (
              <TableCell
                key={headCell.id}
                align={headCell.align}
                sortDirection={!headCell.sortable && orderBy === headCell.id ? order : false}
                sx={{ whiteSpace: 'nowrap' }}
              >
                {headCell.sortable === false ? (
                  headCell.label
                ) : (
                  <TableSortLabel
                    active={orderBy === headCell.id}
                    direction={orderBy === headCell.id ? order : 'asc'}
                    onClick={() => handleRequestSort(headCell.id)}
                  >
                    {headCell.label}
                    {orderBy === headCell.id ? (
                      <Box
                        component="span"
                        sx={{
                          border: 0,
                          clip: 'rect(0 0 0 0)',
                          height: 1,
                          margin: -1,
                          overflow: 'hidden',
                          padding: 0,
                          position: 'absolute',
                          width: 1,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                      </Box>
                    ) : null}
                  </TableSortLabel>
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {stableSort(rows, getComparator(order, orderBy)).map((row) => {
            const isItemSelected = isSelected(row.id);
            return row.trackUseCustom === true ? (
              <Tooltip title={translate(`${rootT}.tooltipText`)} arrow>
                <TableRow
                  hover={!row.trackUseCustom}
                  onClick={() => {
                    if (!row.trackUseCustom) handleClick(row.id);
                  }}
                  role="checkbox"
                  aria-checked={isItemSelected}
                  tabIndex={-1}
                  key={row.id}
                  sx={{
                    '&:hover': {
                      backgroundColor: row.trackUseCustom ? 'inherit' : '#f5f5f5',
                    },
                    opacity: row.trackUseCustom ? 0.5 : 1,
                    cursor: row.trackUseCustom ? 'not-allowed' : 'pointer',
                    backgroundColor: row.trackUseCustom ? '#f5f5f5' : 'inherit',
                    '& .MuiTableCell-root': {
                      color: row.trackUseCustom ? 'rgba(0, 0, 0, 0.38)' : 'inherit',
                    },
                  }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      color="primary"
                      checked={isItemSelected}
                      disabled={row.trackUseCustom === true}
                      inputProps={{ 'aria-labelledby': `enhanced-table-checkbox-${row.id}` }}
                    />
                  </TableCell>

                  <TableCell
                    style={{
                      maxWidth: 100,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {row.trackTitle}
                  </TableCell>
                  <TableCell>{row.trackArtists}</TableCell>
                  <TableCell
                    style={{
                      width: '0%',
                      padding: '4px',
                      justifyContent: 'start',
                      flexDirection: 'column',
                    }}
                  >
                    <Grid container direction="column">
                      <Box textAlign="center" width={1}>
                        {getTrackUsage(row.trackUsage).length === 0 ? null : (
                          <UseDurationChips
                            list={getTrackUsage(row.trackUsage)}
                            darker
                            size="md"
                            toolTip={
                              row.trackUsage?.[0]?.requestedType === 'OTHER' ? (
                                <span style={{ whiteSpace: 'pre-line' }}>{row.trackUsage[0]?.notes}</span>
                              ) : (
                                ''
                              )
                            }
                          />
                        )}
                      </Box>
                    </Grid>
                  </TableCell>
                  <TableCell>{convert(row.trackApprovedFees)}</TableCell>
                  <TableCell>{row.trackSceneAsset}</TableCell>
                </TableRow>
              </Tooltip>
            ) : (
              <TableRow
                hover={!row.trackUseCustom}
                onClick={() => {
                  if (!row.trackUseCustom) handleClick(!isSelected(row.id), row.id);
                }}
                role="checkbox"
                aria-checked={isItemSelected}
                tabIndex={-1}
                key={row.id}
                sx={{
                  '&:hover': {
                    backgroundColor: row.trackUseCustom ? 'inherit' : '#f5f5f5',
                  },
                  opacity: row.trackUseCustom ? 0.5 : 1,
                  cursor: row.trackUseCustom ? 'not-allowed' : 'pointer',
                  backgroundColor: row.trackUseCustom ? '#f5f5f5' : 'inherit',
                  '& .MuiTableCell-root': {
                    color: row.trackUseCustom ? 'rgba(0, 0, 0, 0.38)' : 'inherit',
                  },
                }}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    color="primary"
                    checked={isItemSelected}
                    disabled={row.trackUseCustom === true}
                    inputProps={{ 'aria-labelledby': `enhanced-table-checkbox-${row.id}` }}
                  />
                </TableCell>

                <TableCell
                  style={{
                    maxWidth: 100,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {row.trackTitle}
                </TableCell>
                <TableCell>{row.trackArtists}</TableCell>
                <TableCell
                  style={{
                    width: '0%',
                    padding: '4px',
                    justifyContent: 'start',
                    flexDirection: 'column',
                  }}
                >
                  <Grid container direction="column">
                    <Box textAlign="center" width={1}>
                      {getTrackUsage(row.trackUsage).length === 0 ? null : (
                        <UseDurationChips
                          list={getTrackUsage(row.trackUsage)}
                          darker
                          size="md"
                          toolTip={
                            row.trackUsage?.[0]?.requestedType === 'OTHER' ? (
                              <span style={{ whiteSpace: 'pre-line' }}>{row.trackUsage[0]?.notes}</span>
                            ) : (
                              ''
                            )
                          }
                        />
                      )}
                    </Box>
                  </Grid>
                </TableCell>
                <TableCell>{convert(row.trackApprovedFees)}</TableCell>
                <TableCell>{row.trackSceneAsset}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default TrackList;
